/**
 * Projects-domain mutations — China B-mono P2 seam, Task 3.
 *
 * Converges every write from the projects screens (Designations/Finance,
 * Milestones, the project header, and the client profile page) behind this
 * module, mirroring `adminMutations.ts`. All writes go through the REST
 * client (kronos-api). See docs/kb/architecture/data-mutation-module-pattern.md.
 *
 * The server stamps createdAt/updatedAt (Admin SDK) so the client omits them;
 * other timestamp fields (programHistory startDate, milestone validationDate,
 * targetDate) travel as real Timestamps — apiPost/apiPut
 * JSON-encode them to the {type:'firestore/timestamp/1.0',…} shape the
 * server revives. Update PUTs send only the changed fields; the server
 * merges the patch over the existing doc before writing.
 */
import { Timestamp } from 'firebase/firestore';
import type {
  AvantProjet,
  ChecklistItem,
  FvDeliverable,
  MilestoneDecision,
  ProgramPhase,
  ProgramType,
  ProjectTeam,
} from '@altyor-kronos/shared-types';
import { flattenTeam } from '@altyor-kronos/shared-types';
import { PROGRAM_TEMPLATES } from '../../features/projects/programTemplates';
import type { parseDesignationForm } from '../../features/projects/designations';
import { apiPost, apiPut, apiDelete } from './restClient';

type ParsedDesignation = ReturnType<typeof parseDesignationForm>;

// --- project create (ProjectsHub.tsx submitProject) ---

export interface CreateProjectInput {
  code: string;
  name: string;
  clientId: string;
  program: ProgramType;
  team: ProjectTeam;
  links: { sharepointLink: string; teamsLink: string; customerWebsite: string };
  createdBy: string;
}

export async function createProject(input: CreateProjectInput): Promise<string> {
  const payload = {
    code: input.code,
    name: input.name,
    clientId: input.clientId,
    status: 'active',
    currentProgram: input.program,
    // NB: serverTimestamp() cannot be used inside an array element — Firestore
    // sentinels only resolve at top-level or inside a map. Use Timestamp.now()
    // for array-embedded timestamps; top-level timestamps still use server time.
    programHistory: [{ type: input.program, startDate: Timestamp.now() }],
    team: input.team,
    allMemberUids: flattenTeam(input.team),
    // Only persist links that are non-empty — Firestore is schemaless and
    // storing empty strings clutters the doc with noise.
    ...(input.links.sharepointLink && { sharepointLink: input.links.sharepointLink }),
    ...(input.links.teamsLink && { teamsLink: input.links.teamsLink }),
    ...(input.links.customerWebsite && { customerWebsite: input.links.customerWebsite }),
    createdBy: input.createdBy,
  };
  const res = await apiPost<{ id: string }>('/projects', payload);
  return res.id;
}

/**
 * The `programHistory` a project should carry once it switches to `next`.
 *
 * `ProjectProgramTimeline` reads the last phase **without** an `endDate` as the
 * ongoing one, so changing the program means closing the running phase and
 * opening a new one. Writing `currentProgram` alone leaves the frieze showing
 * the previous phase for good — the two fields drift apart silently.
 *
 * Returns `null` when the running phase is already `next`, so the caller leaves
 * `programHistory` out of its patch rather than rewriting an identical array.
 * A project with no history at all (the Clockify-migrated estate) gets its
 * first phase opened here.
 */
export function advanceProgramHistory(
  history: readonly ProgramPhase[] | undefined,
  next: ProgramType,
  now: Timestamp,
): ProgramPhase[] | null {
  const past = history ?? [];
  const running = past.at(-1);
  const isRunning = running !== undefined && !running.endDate;
  if (isRunning && running.type === next) return null;
  const closed = isRunning ? [...past.slice(0, -1), { ...running, endDate: now }] : [...past];
  return [...closed, { type: next, startDate: now }];
}

// --- milestones (ProjectMilestonesTab.tsx) ---

export async function createMilestone(
  projectId: string,
  input: { name: string; order: number },
): Promise<void> {
  const payload = { name: input.name, order: input.order, status: 'pending', checklist: [] };
  await apiPost(`/projects/${projectId}/milestones`, payload);
}

/**
 * Sème les jalons du gabarit PAD du programme choisi (écran 1 de la maquette).
 * `templateId` survit dans le doc : c'est la clé de dédoublonnage de la
 * moulinette de reprise du PAD, l'id Firestore restant auto-généré.
 *
 * Rend la correspondance `templateId` → id du document créé. Le `templateId`
 * n'EST PAS l'id du document ici (seule `tools/pad/import.ts` écrit sous
 * `milestones/{templateId}`) : sans ce retour, un appelant qui veut poser une
 * date sur « le jalon SOP » n'a aucun moyen de le désigner.
 */
export async function seedProgramMilestones(
  projectId: string,
  program: ProgramType,
): Promise<Record<string, string>> {
  // ponytail: N POST unitaires, comme seedDefaultMilestones avant — non atomique,
  // ids auto. Un endpoint batch seulement si la création devient lente.
  const template = PROGRAM_TEMPLATES[program] ?? [];
  const ids: Record<string, string> = {};
  for (const [order, ms] of template.entries()) {
    const res = await apiPost<{ id: string }>(`/projects/${projectId}/milestones`, {
      name: ms.name,
      templateId: ms.templateId,
      order,
      status: 'pending',
      checklist: [],
      ...(ms.reviewName && {
        reviewName: ms.reviewName,
        decision: 'pending',
        deliverables: ms.deliverables.map((name) => ({ name, status: 'not-done' as const })),
      }),
    });
    ids[ms.templateId] = res.id;
  }
  return ids;
}

export async function updateMilestoneDeliverables(
  projectId: string,
  milestoneId: string,
  deliverables: FvDeliverable[],
): Promise<void> {
  await apiPut(`/projects/${projectId}/milestones/${milestoneId}`, { deliverables });
}

export async function rescheduleMilestone(
  projectId: string,
  milestoneId: string,
  date: Date | null,
): Promise<void> {
  await apiPut(`/projects/${projectId}/milestones/${milestoneId}`, {
    rescheduledDate: date ? Timestamp.fromDate(date) : null,
  });
}

export async function decideMilestone(
  projectId: string,
  milestoneId: string,
  decision: MilestoneDecision,
  comment?: string,
): Promise<void> {
  // `pass` et `derogated` franchissent le jalon ; `not-pass` le laisse ouvert.
  await apiPut(`/projects/${projectId}/milestones/${milestoneId}`, {
    decision,
    decisionDate: Timestamp.now(),
    ...(comment && { comment }),
    ...(decision !== 'not-pass' && { status: 'done', validationDate: Timestamp.now() }),
  });
}

export async function updateMilestoneChecklist(
  projectId: string,
  milestoneId: string,
  checklist: ChecklistItem[],
): Promise<void> {
  await apiPut(`/projects/${projectId}/milestones/${milestoneId}`, { checklist });
}

export async function validateMilestone(projectId: string, milestoneId: string): Promise<void> {
  // ponytail: validationDate is client-time (Timestamp.now()) under REST — the
  // update route only server-stamps updatedAt. Sub-second skew, harmless for a
  // milestone validation date. Same precedent as CalendarView's Timestamp.now().
  await apiPut(`/projects/${projectId}/milestones/${milestoneId}`, {
    status: 'done',
    validationDate: Timestamp.now(),
  });
}

/**
 * Rouvre un jalon franchi — le toggle des jalons simples ET la réouverture d'une
 * revue. La décision est remise à `pending` dans les deux cas : sur un jalon sans
 * revue elle est absente (écrire `pending` ne coûte rien), sur une revue la
 * laisser à `pass` afficherait un jalon rouvert comme encore validé.
 *
 * `comment` porte la trace de la réouverture. L'appelant y concatène l'existant :
 * rouvrir doit laisser une trace, pas effacer celle de la décision annulée.
 */
export async function reopenMilestone(
  projectId: string,
  milestoneId: string,
  comment?: string,
): Promise<void> {
  await apiPut(`/projects/${projectId}/milestones/${milestoneId}`, {
    status: 'pending',
    validationDate: null,
    decision: 'pending',
    decisionDate: null,
    ...(comment && { comment }),
  });
}

export async function updateMilestoneTargetDate(
  projectId: string,
  milestoneId: string,
  dateStr: string,
): Promise<void> {
  const value = dateStr ? Timestamp.fromDate(new Date(dateStr)) : null;
  await apiPut(`/projects/${projectId}/milestones/${milestoneId}`, { targetDate: value });
}

// --- designations — full form (DesignationsView.tsx, Phase 5) ---

/** Payload shared by createDesignation + createDesignations (full-form create). */
function fullDesignationPayload(parsed: ParsedDesignation, variant: ParsedDesignation['variant']) {
  return {
    section: parsed.section,
    category: parsed.category ?? null,
    name: parsed.name,
    billingMode: parsed.billingMode,
    hoursSold: parsed.hoursSold ?? null,
    hourlyRateOverride: parsed.hourlyRateOverride ?? null,
    initialOffer: parsed.initialOffer,
    orderedAmount: 0,
    subContracting: false,
    status: 'pending',
    hoursSpent: 0,
    remainingHours: parsed.hoursSold ?? 0,
    percentCompletion: 0,
    provision: 0,
    catalogId: parsed.catalogId ?? null,
    variant,
    isCustom: parsed.isCustom ?? false,
  };
}

export async function createDesignation(projectId: string, parsed: ParsedDesignation): Promise<void> {
  const payload = fullDesignationPayload(parsed, parsed.variant ?? null);
  await apiPost(`/projects/${projectId}/designations`, payload);
}

export async function updateDesignation(
  projectId: string,
  id: string,
  parsed: ParsedDesignation,
): Promise<void> {
  const patch = {
    section: parsed.section,
    category: parsed.category ?? null,
    name: parsed.name,
    billingMode: parsed.billingMode,
    hoursSold: parsed.hoursSold ?? null,
    hourlyRateOverride: parsed.hourlyRateOverride ?? null,
    initialOffer: parsed.initialOffer,
  };
  await apiPut(`/projects/${projectId}/designations/${id}`, patch);
}

/**
 * Sequential creates (one POST per entry) — verbatim move of the
 * `handleSubmitMultiple` for-loop. Note `variant` is written as-is here
 * (no `?? null` fallback), unlike `createDesignation` — that asymmetry
 * exists in the source and is preserved rather than "fixed".
 */
export async function createDesignations(
  projectId: string,
  parsedList: readonly ParsedDesignation[],
): Promise<void> {
  for (const parsed of parsedList) {
    const payload = fullDesignationPayload(parsed, parsed.variant);
    await apiPost(`/projects/${projectId}/designations`, payload);
  }
}

/** Shared by DesignationsView + FinanceTable — identical write in both. */
export async function deleteDesignation(projectId: string, id: string): Promise<void> {
  await apiDelete(`/projects/${projectId}/designations/${id}`);
}

// --- designations — header-only form (FinanceTable.tsx master-detail) ---

/** Payload shared by createDesignationHeader + createDesignationHeaders. */
function headerDesignationPayload(parsed: ParsedDesignation, variant: ParsedDesignation['variant']) {
  const isHourly = parsed.billingMode === 'hourly';
  return {
    section: parsed.section,
    category: parsed.category ?? null,
    name: parsed.name,
    billingMode: parsed.billingMode,
    ...(isHourly
      ? {
          hoursSold: parsed.hoursSold,
          hourlyRateOverride: parsed.hourlyRateOverride ?? null,
          hoursSpent: 0,
          remainingHours: parsed.hoursSold,
          percentCompletion: 0,
        }
      : {}),
    initialOffer: 0,
    orderedAmount: 0,
    subContracting: false,
    status: 'pending',
    provision: 0,
    catalogId: parsed.catalogId ?? null,
    variant,
    isCustom: parsed.isCustom ?? false,
  };
}

export async function createDesignationHeader(
  projectId: string,
  parsed: ParsedDesignation,
): Promise<void> {
  const payload = headerDesignationPayload(parsed, parsed.variant ?? null);
  await apiPost(`/projects/${projectId}/designations`, payload);
}

export async function updateDesignationHeader(
  projectId: string,
  id: string,
  parsed: ParsedDesignation,
): Promise<void> {
  const isHourly = parsed.billingMode === 'hourly';
  const patch = {
    section: parsed.section,
    category: parsed.category ?? null,
    name: parsed.name,
    ...(isHourly
      ? {
          hoursSold: parsed.hoursSold,
          hourlyRateOverride: parsed.hourlyRateOverride ?? null,
        }
      : {}),
  };
  await apiPut(`/projects/${projectId}/designations/${id}`, patch);
}

/**
 * Verbatim move of `handleHeaderSubmitMultiple` — same asymmetry as
 * `createDesignations` (`variant` has no `?? null` fallback here).
 */
export async function createDesignationHeaders(
  projectId: string,
  parsedList: readonly ParsedDesignation[],
): Promise<void> {
  for (const parsed of parsedList) {
    const payload = headerDesignationPayload(parsed, parsed.variant);
    await apiPost(`/projects/${projectId}/designations`, payload);
  }
}

/**
 * Generic click-to-edit cell commit + the rate-override commit — every
 * branch of `FinanceTable`'s `commitEdit`/`onRateOverrideCommit` already
 * reduced to the same shape before calling `updateDoc`: a partial fields
 * object plus `updatedAt: serverTimestamp()`. Parsing/validation of the
 * raw cell input stays in the component; this is the verbatim write.
 */
export async function updateDesignationFields(
  projectId: string,
  id: string,
  fields: Record<string, unknown>,
): Promise<void> {
  await apiPut(`/projects/${projectId}/designations/${id}`, fields);
}

// --- invoices (FinanceTable.tsx invoicing block) ---

export async function createInvoice(
  projectId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  await apiPost(`/projects/${projectId}/invoices`, payload);
}

export async function updateInvoice(
  projectId: string,
  id: string,
  payload: Record<string, unknown>,
): Promise<void> {
  await apiPut(`/projects/${projectId}/invoices/${id}`, payload);
}

export async function deleteInvoice(projectId: string, id: string): Promise<void> {
  await apiDelete(`/projects/${projectId}/invoices/${id}`);
}

// --- designation auto-attach (TimesheetView.tsx resolveDesignationId) ---

/**
 * Verbatim move of `TimesheetView.resolveDesignationId`'s auto-attach write —
 * fires when the timesheet "add row" picker selects a catalog entry that has
 * no matching designation on the project yet. Distinct payload shape from
 * `createDesignation` (no `billingMode`/`hourlyRateOverride`, `hoursSold`
 * defaults to 0 rather than `null`) — kept as its own function rather than
 * force-fit into an existing one. Returns the new doc id, same contract as
 * the original inline create.
 */
export async function attachCatalogDesignation(
  projectId: string,
  input: { section: string; name: string; catalogId: string; variant: string | null },
): Promise<string> {
  const payload = {
    section: input.section,
    category: null,
    name: input.name,
    initialOffer: 0,
    orderedAmount: 0,
    subContracting: false,
    status: 'pending',
    hoursSold: 0,
    hoursSpent: 0,
    remainingHours: 0,
    percentCompletion: 0,
    provision: 0,
    catalogId: input.catalogId,
    variant: input.variant,
    isCustom: false,
  };
  const res = await apiPost<{ id: string }>(`/projects/${projectId}/designations`, payload);
  return res.id;
}

// --- project header (ProjectDetail.tsx) ---

export async function updateProject(id: string, data: Record<string, unknown>): Promise<void> {
  await apiPut(`/projects/${id}`, data);
}

/**
 * Écrit le bloc avant-projet **en entier**. Pas de patch partiel : le bloc est
 * un formulaire, l'écran en détient toujours l'état complet, et un merge
 * partiel rendrait impossible la suppression d'une ligne de risque.
 */
export async function updateAvantProjet(
  projectId: string,
  avantProjet: AvantProjet,
): Promise<void> {
  await apiPut(`/projects/${projectId}`, { avantProjet });
}
